import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { householdsApi } from 'src/Services/API';
import {
  trackHouseholdCreated,
  trackHouseholdDeleted,
  trackHouseholdUpdated,
} from 'src/Services/Mixpanel/events';
import { CreateHouseholdDto, HouseholdDto, UpdateHouseholdDto } from 'src/types/household.types';

const invalidateHouseholds = (queryClient: ReturnType<typeof useQueryClient>, assetId?: string) => {
  queryClient.invalidateQueries({ queryKey: ['fetch-households', assetId] });
  queryClient.invalidateQueries({ queryKey: ['fetch-members', assetId] });
  queryClient.invalidateQueries({ queryKey: ['fetch-eligible-household-members', assetId] });
};

export const useCreateHousehold = (
  options?: UseMutationOptions<HouseholdDto, unknown, CreateHouseholdDto>
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<HouseholdDto, unknown, CreateHouseholdDto>(
    ['create-household', assetId],
    async payload => {
      if (!assetId) throw new Error('No asset selected');
      return householdsApi.create(assetId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackHouseholdCreated();
        invalidateHouseholds(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useUpdateHousehold = (
  options?: UseMutationOptions<
    HouseholdDto,
    unknown,
    { householdId: string; payload: UpdateHouseholdDto }
  >
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<HouseholdDto, unknown, { householdId: string; payload: UpdateHouseholdDto }>(
    ['update-household', assetId],
    async ({ householdId, payload }) => {
      if (!assetId) throw new Error('No asset selected');
      return householdsApi.update(assetId, householdId, payload);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackHouseholdUpdated();
        invalidateHouseholds(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useDeleteHousehold = (options?: UseMutationOptions<void, unknown, string>) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>(
    ['delete-household', assetId],
    async householdId => {
      if (!assetId) throw new Error('No asset selected');
      await householdsApi.delete(assetId, householdId);
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackHouseholdDeleted();
        invalidateHouseholds(queryClient, assetId);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};
